// Social marketing composer (§227) — draft posts with AI (or by hand), save to a library,
// copy/paste to Facebook/Instagram, and mark posted. AI drafting reuses the ai_marketing_copy
// quota (§224). Auto-publish to the Facebook Graph API is a dormant backend follow-up.
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Megaphone, Sparkles, Copy, Check, Trash2, ExternalLink } from 'lucide-react'
import { socialApi, type SocialPost, type SocialPlatform } from '../lib/api'
import { apiError } from '../lib/errors'

const PLATFORMS: { id: SocialPlatform; label: string }[] = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'general', label: 'General' },
  { id: 'other', label: 'Other' },
]

const OPEN_URL: Partial<Record<SocialPlatform, string>> = {
  facebook: 'https://www.facebook.com/',
  instagram: 'https://www.instagram.com/',
}

const card: React.CSSProperties = {
  background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
  borderRadius: 12, padding: 16, marginBottom: 16,
}
const inp: React.CSSProperties = {
  width: '100%', padding: '9px 11px', fontSize: 13, borderRadius: 8,
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
}
function btn(bg: string): React.CSSProperties {
  return { padding: '9px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: bg, color: '#fff' }
}

const STATUS_COLOR: Record<string, string> = { draft: '#6B7280', posted: '#2D6A2F', scheduled: '#B45309' }

export default function MarketingPage() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['social'], queryFn: () => socialApi.list().then(r => r.data.posts) })
  const posts = data ?? []

  const [platform, setPlatform] = useState<SocialPlatform>('facebook')
  const [topic, setTopic] = useState('')
  const [body, setBody] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [drafted, setDrafted] = useState(false)
  const [err, setErr] = useState('')

  const draft = useMutation({
    mutationFn: () => socialApi.draft(topic.trim(), platform).then(r => r.data),
    onSuccess: (r) => { setBody(r.draft); setDrafted(true); setErr('') },
    onError: (e) => setErr(apiError(e, 'Drafting failed')),
  })
  const save = useMutation({
    mutationFn: () => socialApi.create({ platform, body: body.trim(), image_url: imageUrl.trim() || undefined, source: drafted ? 'ai' : 'manual', status: 'draft' }).then(r => r.data),
    onSuccess: () => { setTopic(''); setBody(''); setImageUrl(''); setDrafted(false); setErr(''); qc.invalidateQueries({ queryKey: ['social'] }) },
    onError: (e) => setErr(apiError(e, 'Save failed')),
  })

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Megaphone size={22} color="var(--color-primary)" />
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>Marketing</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 20 }}>
        Draft social posts with AI, save them to your library, then copy/paste to Facebook or Instagram.
      </p>

      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', marginBottom: 12 }}>New post</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {PLATFORMS.map(p => (
            <button key={p.id} type="button" onClick={() => setPlatform(p.id)} style={{
              padding: '6px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
              border: '1px solid var(--color-border)',
              background: platform === p.id ? 'var(--color-primary)' : 'transparent',
              color: platform === p.id ? '#fff' : 'var(--color-text-muted)',
            }}>{p.label}</button>
          ))}
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px dashed var(--color-border)', marginBottom: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Sparkles size={13} /> Draft with AI
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={topic} onChange={e => setTopic(e.target.value)}
              placeholder="e.g. Kent mangoes ready this weekend, farm stand open Sat–Sun 9–2"
              style={{ ...inp, flex: 1 }} />
            <button type="button" onClick={() => draft.mutate()} disabled={draft.isPending || !topic.trim()} style={btn('var(--color-accent)')}>
              {draft.isPending ? '…' : 'Draft'}
            </button>
          </div>
        </div>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Post text</span>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={5}
            placeholder="Write your post, or draft it with AI above." style={{ ...inp, resize: 'vertical', marginTop: 4 }} />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Image URL (optional)</span>
          <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://…" style={{ ...inp, marginTop: 4 }} />
        </label>
        {err && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => save.mutate()} disabled={save.isPending || !body.trim()} style={btn('var(--color-primary)')}>
            {save.isPending ? 'Saving…' : 'Save to library'}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>Library</div>
      {posts.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
          No posts yet. Draft one above.
        </div>
      ) : posts.map(p => <PostCard key={p.id} post={p} />)}
    </main>
  )
}

function PostCard({ post }: { post: SocialPost }) {
  const qc = useQueryClient()
  const [copied, setCopied] = useState(false)
  const markPosted = useMutation({ mutationFn: () => socialApi.markPosted(post.id), onSuccess: () => qc.invalidateQueries({ queryKey: ['social'] }) })
  const remove = useMutation({ mutationFn: () => socialApi.remove(post.id), onSuccess: () => qc.invalidateQueries({ queryKey: ['social'] }) })

  const copy = () => {
    navigator.clipboard?.writeText(post.body).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) }).catch(() => {})
  }
  const openUrl = OPEN_URL[post.platform]

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', color: 'var(--color-text-muted)' }}>{post.platform}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 6, textTransform: 'uppercase', color: '#fff', background: STATUS_COLOR[post.status] || '#6B7280' }}>{post.status}</span>
        {post.source === 'ai' && <span style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>· auto-drafted</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-faint)' }}>{new Date(post.created_at).toLocaleDateString()}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text)', whiteSpace: 'pre-wrap', marginBottom: 10 }}>{post.body}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={copy} style={{ ...btn('transparent'), color: 'var(--color-text)', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 5 }}>
          {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
        </button>
        {openUrl && (
          <a href={openUrl} target="_blank" rel="noopener noreferrer"
            style={{ ...btn('transparent'), color: 'var(--color-text)', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}>
            <ExternalLink size={13} /> Open {post.platform === 'instagram' ? 'Instagram' : 'Facebook'}
          </a>
        )}
        {post.status !== 'posted' && (
          <button onClick={() => markPosted.mutate()} disabled={markPosted.isPending} style={btn('var(--color-primary)')}>Mark posted</button>
        )}
        <button onClick={() => { if (confirm('Delete this post?')) remove.mutate() }} disabled={remove.isPending}
          style={{ ...btn('transparent'), color: '#dc2626', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  )
}
